import {
  TicketPriority,
  TicketStatus,
  TicketUser,
} from "@jugl-web/rest-api/tickets/models/common-types";
import { useTranslations } from "@jugl-web/utils";
import { joinReactNodesInHumanReadableForm } from "@jugl-web/utils/utils/joinReactNodes";
import { ReactNode, useCallback, useMemo } from "react";
import { useTicketPriorities } from "../../../hooks/useTicketPriorities";
import { useTicketStatuses } from "../../../hooks/useTicketStatuses";
import { formatTicketUserName } from "../../../utils/formatTicketUserName";

const MAX_NAMED_TICKET_ASSIGNEES = 3;

export const useTicketActivityMessageRenderers = () => {
  const { t } = useTranslations();
  const { getStatusById } = useTicketStatuses();
  const { getPriorityById } = useTicketPriorities();

  const getStatusLabel = useCallback(
    (status: TicketStatus) => getStatusById(status).label,
    [getStatusById]
  );

  const getPriorityLabel = useCallback(
    (priority: TicketPriority) => getPriorityById(priority).label,
    [getPriorityById]
  );

  const formatAssigneesLabel = useCallback(
    (assignees: TicketUser[]) => {
      if (assignees.length > MAX_NAMED_TICKET_ASSIGNEES) {
        return (
          <b>
            {t(
              {
                id: "client-support-page.ticket-history-assignees-count",
                defaultMessage: "{count} Users",
              },
              { count: assignees.length }
            )}
          </b>
        );
      }

      return joinReactNodesInHumanReadableForm(
        assignees.map((assignee) => (
          <b key={assignee.user_id}>{formatTicketUserName(assignee)}</b>
        ))
      );
    },
    [t]
  );

  const ticketCreatedMessageRenderer = useCallback(
    (contactName: string, actorName: string) =>
      t(
        {
          id: "client-support-page.ticket-history-created",
          defaultMessage: "Ticket for {contactName} was created by {actorName}",
        },
        {
          contactName: <b>{contactName}</b>,
          actorName,
        }
      ),
    [t]
  );

  const customerUpdatedMessageRenderer = useCallback(
    (contactName: string, actorName: string) =>
      t(
        {
          id: "client-support-page.ticket-history-customer-updated",
          defaultMessage:
            "Ticket Customer was updated to {contactName} by {actorName}",
        },
        {
          contactName: <b>{contactName}</b>,
          actorName,
        }
      ),
    [t]
  );

  const assigneesAddedMessageRenderer = useCallback(
    (assignees: TicketUser[], actorName: string) => {
      const verb =
        assignees.length === 1
          ? t({
              id: "client-support-page.ticket-history-was-assigned",
              defaultMessage: "was assigned to the Ticket",
            })
          : t({
              id: "client-support-page.ticket-history-were-assigned",
              defaultMessage: "were assigned to the Ticket",
            });

      return t(
        {
          id: "client-support-page.ticket-history-assignees-added",
          defaultMessage: "{assignees} {verb} by {actorName}",
        },
        {
          assignees: formatAssigneesLabel(assignees),
          verb,
          actorName,
        }
      );
    },
    [formatAssigneesLabel, t]
  );

  const assigneesRemovedMessageRenderer = useCallback(
    (assignees: TicketUser[], actorName: string) => {
      const verb =
        assignees.length === 1
          ? t({
              id: "client-support-page.ticket-history-was-unassigned",
              defaultMessage: "was unassigned from the Ticket",
            })
          : t({
              id: "client-support-page.ticket-history-were-unassigned",
              defaultMessage: "were unassigned from the Ticket",
            });

      return t(
        {
          id: "client-support-page.ticket-history-assignees-removed",
          defaultMessage: "{assignees} {verb} by {actorName}",
        },
        {
          assignees: formatAssigneesLabel(assignees),
          verb,
          actorName,
        }
      );
    },
    [formatAssigneesLabel, t]
  );

  const dueDateSetMessageRenderer = useCallback(
    (dueDateLabel: ReactNode, actorName: string) =>
      t(
        {
          id: "client-support-page.ticket-history-due-date-set",
          defaultMessage: "Due date set to {dueDate} by {actorName}",
        },
        {
          dueDate: dueDateLabel,
          actorName,
        }
      ),
    [t]
  );

  const prioritySetMessageRenderer = useCallback(
    (priorityLabel: ReactNode, actorName: string) =>
      t(
        {
          id: "client-support-page.ticket-history-priority-set",
          defaultMessage: "Priority set to {priority} by {actorName}",
        },
        {
          priority: priorityLabel,
          actorName,
        }
      ),
    [t]
  );

  const statusSetMessageRenderer = useCallback(
    (statusLabel: ReactNode, actorName: string) =>
      t(
        {
          id: "client-support-page.ticket-history-status-set",
          defaultMessage: "Status set to {status} by {actorName}",
        },
        {
          status: statusLabel,
          actorName,
        }
      ),
    [t]
  );

  const fallbackUpdatedMessageRenderer = useCallback(
    (actorName: string) =>
      t(
        {
          id: "client-support-page.ticket-history-generic-updated",
          defaultMessage: "Ticket was updated by {actorName}",
        },
        { actorName }
      ),
    [t]
  );

  return useMemo(
    () => ({
      ticketCreatedMessageRenderer,
      customerUpdatedMessageRenderer,
      assigneesAddedMessageRenderer,
      assigneesRemovedMessageRenderer,
      dueDateSetMessageRenderer,
      prioritySetMessageRenderer,
      statusSetMessageRenderer,
      fallbackUpdatedMessageRenderer,
      getStatusLabel,
      getPriorityLabel,
    }),
    [
      assigneesAddedMessageRenderer,
      assigneesRemovedMessageRenderer,
      customerUpdatedMessageRenderer,
      dueDateSetMessageRenderer,
      fallbackUpdatedMessageRenderer,
      getPriorityLabel,
      getStatusLabel,
      prioritySetMessageRenderer,
      statusSetMessageRenderer,
      ticketCreatedMessageRenderer,
    ]
  );
};
